if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        this.unlockBackgroundScroll();
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.lockBackgroundScroll();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      lockBackgroundScroll() {
        if (window.scrollSmoother) {
          this.smootherWasPaused = window.scrollSmoother.paused();
          window.scrollSmoother.paused(true);
        }

        if (typeof ScrollTrigger !== 'undefined') {
          try {
            ScrollTrigger.normalizeScroll(false);
          } catch (error) {
            // normalizeScroll may already be off
          }
        }

        if (this.boundPreventBackdropScroll) return;
        this.boundPreventBackdropScroll = this.preventBackdropScroll.bind(this);
        this.boundScrollModal = this.scrollModal.bind(this);
        this.lastTouchY = null;
        document.addEventListener('wheel', this.boundScrollModal, { capture: true, passive: false });
        document.addEventListener('touchstart', this.boundScrollModal, { capture: true, passive: true });
        document.addEventListener('touchmove', this.boundScrollModal, { capture: true, passive: false });
        this.addEventListener('wheel', this.boundPreventBackdropScroll, { passive: false });
        this.addEventListener('touchmove', this.boundPreventBackdropScroll, { passive: false });
      }

      unlockBackgroundScroll() {
        if (window.scrollSmoother && !this.smootherWasPaused) {
          window.scrollSmoother.paused(false);
        }

        if (typeof ScrollTrigger !== 'undefined') {
          try {
            ScrollTrigger.normalizeScroll(true);
          } catch (error) {
            // ignore
          }
        }

        if (this.boundScrollModal) {
          document.removeEventListener('wheel', this.boundScrollModal, { capture: true });
          document.removeEventListener('touchstart', this.boundScrollModal, { capture: true });
          document.removeEventListener('touchmove', this.boundScrollModal, { capture: true });
          this.boundScrollModal = null;
        }

        if (!this.boundPreventBackdropScroll) return;
        this.removeEventListener('wheel', this.boundPreventBackdropScroll);
        this.removeEventListener('touchmove', this.boundPreventBackdropScroll);
        this.boundPreventBackdropScroll = null;
        this.lastTouchY = null;
        this.lastTouchX = null;
      }

      getModalScroller() {
        const content = this.querySelector('.quick-add-modal__content');
        if (!content) return null;
        const info = content.querySelector('.quick-add-modal__content-info');
        if (info && info.scrollHeight > info.clientHeight + 1) return info;
        return content;
      }

      canScrollVertically(element, delta) {
        if (!(element instanceof Element)) return false;
        const overflowY = window.getComputedStyle(element).overflowY;
        if (overflowY !== 'auto' && overflowY !== 'scroll') return false;
        if (element.scrollHeight <= element.clientHeight + 1) return false;
        const atTop = element.scrollTop <= 0;
        const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
        if (delta < 0 && atTop) return false;
        if (delta > 0 && atBottom) return false;
        return true;
      }

      innerScrollerFromEvent(event, modalScroller, delta) {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        for (const node of path) {
          if (node === modalScroller) break;
          if (this.canScrollVertically(node, delta)) return node;
        }
        return null;
      }

      scrollModal(event) {
        if (!this.hasAttribute('open')) return;

        if (event.type === 'touchstart') {
          this.lastTouchY = event.touches?.[0]?.clientY ?? null;
          this.lastTouchX = event.touches?.[0]?.clientX ?? null;
          return;
        }

        let delta = 0;
        let deltaX = 0;
        if (event.type === 'wheel') {
          delta = event.deltaY || 0;
          deltaX = event.deltaX || 0;
        } else if (event.type === 'touchmove' && event.touches?.[0]) {
          if (this.lastTouchY != null) {
            delta = this.lastTouchY - event.touches[0].clientY;
            deltaX = (this.lastTouchX ?? event.touches[0].clientX) - event.touches[0].clientX;
          }
          this.lastTouchY = event.touches[0].clientY;
          this.lastTouchX = event.touches[0].clientX;
        }

        if (Math.abs(deltaX) > Math.abs(delta) && event.target.closest?.('.swiper, .slider, .product-image-swiper')) {
          return;
        }

        if (!delta) return;

        const scroller = this.getModalScroller();
        if (!scroller) return;

        if (this.innerScrollerFromEvent(event, scroller, delta)) return;

        scroller.scrollTop += delta;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      preventBackdropScroll(event) {
        if (!event.target.closest('.quick-add-modal__content')) {
          event.preventDefault();
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
